import { gsap } from "gsap";

export type HorizontalLoopConfig = {
    speed?: number;
    reversed?: boolean;
    repeat?: number;
    paused?: boolean;
    /** Marge (px) ajoutée à la fin de la piste pour éviter que dernier / premier item se collent à la boucle. */
    paddingRight?: number;
    snap?: number | false;
};

/** Défaut aligné sur un `gap-3` Tailwind (~12px). */
export const DEFAULT_LOOP_SEAM_GAP_PX = 12;

/** Classe à poser sur chaque enfant direct du flex ciblé par la boucle. */
export const HORIZONTAL_LOOP_ITEM_CLASS = "horizontal-loop-item" as const;

/**
 * Boucle horizontale infinie (GSAP) pour une liste d’éléments en flex.
 * Chaque élément doit être un enfant direct du conteneur flex (sélectionnés via la classe que tu passes au querySelector).
 */
export function horizontalLoop(items: Element[], config: HorizontalLoopConfig = {}) {
    const configFull = config;
    const tl = gsap.timeline({
        repeat: configFull.repeat,
        paused: configFull.paused,
        defaults: { ease: "none" },
        onReverseComplete: () => {
            tl.totalTime(tl.rawTime() + tl.duration() * 100);
        },
    });

    const length = items.length;
    if (length === 0) return tl;

    const startX = (items[0] as HTMLElement).offsetLeft;
    const widths: number[] = [];
    const xPercents: number[] = [];
    const pixelsPerSecond = (configFull.speed || 1) * 100;
    const snap =
        configFull.snap === false ? (v: number) => v : gsap.utils.snap(configFull.snap || 1);

    const paddingRight =
        configFull.paddingRight !== undefined ? configFull.paddingRight : DEFAULT_LOOP_SEAM_GAP_PX;

    gsap.set(items, {
        xPercent: (i, el) => {
            const w = (widths[i] = parseFloat(gsap.getProperty(el, "width", "px") as string));
            xPercents[i] = snap(
                (parseFloat(gsap.getProperty(el, "x", "px") as string) / w) * 100 +
                    (gsap.getProperty(el, "xPercent") as number),
            );
            return xPercents[i];
        },
    });

    gsap.set(items, { x: 0 });

    const totalWidth =
        (items[length - 1] as HTMLElement).offsetLeft +
        (xPercents[length - 1] / 100) * widths[length - 1] -
        startX +
        (items[length - 1] as HTMLElement).offsetWidth *
            (gsap.getProperty(items[length - 1], "scaleX") as number) +
        paddingRight;

    for (let i = 0; i < length; i++) {
        const item = items[i] as HTMLElement;
        const curX = (xPercents[i] / 100) * widths[i];
        const distanceToStart = item.offsetLeft + curX - startX;
        const distanceToLoop = distanceToStart + widths[i] * (gsap.getProperty(item, "scaleX") as number);

        tl.to(
            item,
            {
                xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
                duration: distanceToLoop / pixelsPerSecond,
            },
            0,
        )
            .fromTo(
                item,
                { xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100) },
                {
                    xPercent: xPercents[i],
                    duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                    immediateRender: false,
                },
                distanceToLoop / pixelsPerSecond,
            )
            .add("label" + i, distanceToStart / pixelsPerSecond);
    }

    tl.progress(1, true).progress(0, true);

    if (configFull.reversed) {
        tl.vars.onReverseComplete?.();
        tl.reverse();
    }

    return tl;
}
